/**
 * Wraps an image URL through the proxy if it's an external URL.
 * Use this for all <img src={}> tags that may receive external URLs
 * to avoid CORS/CORP blocking from external servers.
 */
export function proxyImageUrl(url: string | undefined | null): string {
  if (!url) return ''
  if (url.startsWith('/') || url.startsWith('data:') || url.startsWith('blob:')) return url
  if (url.includes('/api/images/proxy')) return url
  // Our own CDN already serves with CORS + cache-control + immutable. Skip
  // the same-origin proxy hop entirely — it adds latency and breaks in local
  // dev when S3 env isn't configured.
  if (/^https:\/\/cdn(-staging)?\.opcgct\.fraccie\.be\//.test(url)) return url
  return `/api/images/proxy?url=${encodeURIComponent(url)}`
}

// Simple queue to manage concurrent image loads
class ImageLoadQueue {
  private queue: Array<() => void> = []
  private loading = 0
  private maxConcurrent = 20 // Increased limit for concurrent image loads

  async add(loadFn: () => Promise<void>) {
    return new Promise<void>((resolve, reject) => {
      const wrappedFn = async () => {
        try {
          this.loading++
          await loadFn()
          resolve()
        } catch (error) {
          reject(error)
        } finally {
          this.loading--
          this.processNext()
        }
      }

      if (this.loading < this.maxConcurrent) {
        wrappedFn()
      } else {
        this.queue.push(wrappedFn)
      }
    })
  }

  private processNext() {
    if (this.queue.length > 0 && this.loading < this.maxConcurrent) {
      const next = this.queue.shift()
      if (next) next()
    }
  }
}

export const imageLoadQueue = new ImageLoadQueue()

// Helper to preload an image
export function preloadImage(src: string): Promise<void> {
  return imageLoadQueue.add(() => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve()
      img.onerror = () => reject(new Error(`Failed to load ${src}`))
      img.src = src
    })
  })
}

// Check if an image URL is likely to be available locally
export function hasLocalVersion(src: string): boolean {
  const patterns = [
    /\/(ST\d+-\d+|OP\d+-\d+|EB\d+-\d+|P-\d+|PRB\d+-\d+)(?:_p\d+)?\.png/i,
  ]
  
  return patterns.some(pattern => pattern.test(src))
}

// Get local path for a card image
export function getLocalCardPath(cardId: string): string {
  // Remove any variant suffixes like _p1, _p2 etc
  const baseId = cardId.replace(/_p\d+$/, '')
  return `/images/cards/${baseId}.png`
}